import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircleIcon, ArrowRightIcon, UserCheckIcon, ShieldCheckIcon, MapPinIcon, CheckCircle2Icon, ChevronDownIcon, StarIcon, HomeIcon, BriefcaseIcon, CheckIcon } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { useLanguage } from '../hooks/useLanguage';
export function IbizaVillaTransfer() {
  const {
    language
  } = useLanguage();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // SEO meta tags
  useEffect(() => {
    document.title = 'Ibiza Villa Transfer | Fixed Price via WhatsApp';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descContent = 'Private villa transfers in Ibiza with local drivers who know gates, remote roads and private entrances. Fixed price, fast WhatsApp quote and 24/7 service.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [language]);
  // WhatsApp URL
  const whatsappUrl = language === 'en' ? 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20a%20villa%20transfer%20in%20Ibiza.' : 'https://wa.me/34613756211?text=Hola%2C%20necesito%20un%20traslado%20a%20una%20villa%20en%20Ibiza.';
  // FAQs Data
  const faqs = [{
    q: 'Do you cover remote villas and fincas in Ibiza?',
    a: 'Yes. We cover villas, fincas and private properties across the island, including rural and remote areas.'
  }, {
    q: 'Can I send the villa address on WhatsApp?',
    a: "Yes. Send the address, passenger count and any access notes, and we'll confirm the fixed price."
  }, {
    q: 'Can I book both arrival and departure transfers?',
    a: 'Yes. Arrival and return villa transfers can be booked together.'
  }, {
    q: 'What if the villa has a gate code or difficult entrance?',
    a: 'Send us the details when booking. Our drivers are used to handling private access points.'
  }, {
    q: 'Do you offer villa transfer for groups?',
    a: 'Yes. We provide vans and coordinated transport for larger groups.'
  }, {
    q: 'Do you provide airport to villa transfer in Ibiza?',
    a: 'Yes. We regularly provide direct airport to villa transfers across Ibiza, including private estates, fincas and remote properties.'
  }];
  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <main className="flex-grow pt-20" data-id="element-796">
      {/* 1. Hero */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] flex items-center bg-navy overflow-hidden" data-id="element-797">
        <div className="absolute inset-0 z-0" data-id="element-798">
          <img src="https://cdn.magicpatterns.com/uploads/nqecG9tigbu6asFbbtJryj/foto_dentro_casa.jpg" alt="Mercedes V-Class premium van parked at a private villa entrance in Ibiza with olive trees and finca in the background" className="w-full h-full object-cover object-[35%_center] md:object-[40%_60%] opacity-70" data-id="element-799" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/50 to-navy/20" data-id="element-800" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 w-full py-12 md:py-20" data-id="element-801">
          <div className="max-w-2xl" data-id="element-802">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-803">
              <div className="inline-flex items-center space-x-1.5 md:space-x-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-5 md:mb-8 border border-white/15" data-id="element-804">
                <span className="text-gold-subtle text-xs md:text-sm" data-id="element-805">★</span>
                <span className="text-white text-[10px] md:text-xs font-semibold tracking-wide" data-id="element-806">
                  Local drivers · Fixed price · No upfront payment · Fast
                  WhatsApp quote
                </span>
              </div>

              <h1 className="text-[1.75rem] md:text-6xl font-heading text-white mb-5 md:mb-8 leading-[1.15] md:leading-[1.1]" data-id="element-807">
                Ibiza Villa Transfer — Fixed Price via WhatsApp
              </h1>

              <p className="text-base md:text-xl text-white/90 mb-8 md:mb-12 leading-relaxed max-w-md md:max-w-xl" data-id="element-808">
                Direct airport to villa transfers across Ibiza with local
                drivers who know private entrances, finca access points and the
                remote roads where GPS often fails.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4" data-id="element-809">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-810">
                  <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-811" />
                  <span data-id="element-812">Get villa transfer price on WhatsApp</span>
                </a>
                <a href="#areas" className="w-full sm:w-auto flex items-center justify-center space-x-2 text-white hover:text-gold-subtle px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors" data-id="element-813">
                  <span data-id="element-814">See villa areas we cover</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-815" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <section className="bg-warm-cream border-y border-stone/10 py-10 md:py-14" data-id="element-816">
        <div className="mx-auto max-w-7xl px-5" data-id="element-817">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8" data-id="element-818">
            {[{
            text: 'Direct to your villa entrance',
            icon: MapPinIcon
          }, {
            text: 'Fixed prices confirmed upfront',
            icon: ShieldCheckIcon
          }, {
            text: 'Local drivers who know Ibiza',
            icon: UserCheckIcon
          }, {
            text: 'Remote access routes covered',
            icon: HomeIcon
          }, {
            text: 'Fast WhatsApp quote',
            icon: MessageCircleIcon
          }].map((item, index) => <div key={index} className="flex flex-col items-center text-center space-y-3" data-id="element-819">
                <div className="w-12 h-12 rounded-full bg-warm-white flex items-center justify-center" data-id="element-820">
                  <item.icon className="w-5 h-5 text-gold-subtle" data-id="element-821" />
                </div>
                <span className="text-xs md:text-sm font-medium text-slate leading-tight" data-id="element-822">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. Problem / Solution */}
      <section className="py-16 md:py-24 bg-white" data-id="element-823">
        <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-824">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-825">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-8" data-id="element-826">
              Why villa transfers in Ibiza often go wrong
            </h2>
            <div className="space-y-6 text-lg text-slate leading-relaxed" data-id="element-827">
              <p data-id="element-828">
                Many villa transfers in Ibiza go wrong because the driver
                depends only on GPS. That creates delays, confusion at private
                gates and calls asking for directions just after landing.
              </p>
              <p data-id="element-829">
                We handle villa transfers differently. Our drivers know Ibiza's
                roads, private entrances, rural access points and the areas
                where maps often fail.
              </p>
              <p className="font-medium text-navy pt-4" data-id="element-830">
                You send the villa address once. We handle the rest.
              </p>
            </div>

            <div className="mt-10" data-id="element-831">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-832">
                <WhatsAppIcon className="w-5 h-5" data-id="element-833" />
                <span data-id="element-834">Get villa transfer price on WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. Villa Areas + Pricing */}
      <section id="areas" className="py-16 md:py-24 bg-warm-white border-y border-stone/10" data-id="element-835">
        <div className="mx-auto max-w-7xl px-5" data-id="element-836">
          {/* Areas Sub-section */}
          <div className="mb-20" data-id="element-837">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-838">
              <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-839">
                Villa transfer areas across Ibiza
              </h2>
              <p className="text-slate leading-relaxed" data-id="element-840">
                We provide villa transfers all across the island, including
                remote villas, fincas and private estates. If your villa is not
                listed below, send us the address on WhatsApp and we'll confirm
                your fixed price.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-841">
              {[{
              zone: 'Ibiza Town & surroundings',
              areas: ['Dalt Vila', 'Talamanca', 'Cap Martinet', 'Roca Llisa', 'Can Furnet']
            }, {
              zone: 'West coast',
              areas: ['San Antonio Bay', 'Cala Tarida', 'Cala Conta', 'Es Cubells']
            }, {
              zone: 'East coast',
              areas: ['Santa Eulalia', 'Es Canar', 'Santa Gertrudis', 'San Carlos']
            }, {
              zone: 'North & rural areas',
              areas: ['Portinatx', 'Sant Joan', 'Sant Miquel', 'Cala Boix', 'Cala de Sant Vicent', 'private fincas']
            }].map((block, index) => <div key={index} className="bg-white border border-stone/10 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-842">
                  <h3 className="text-xl font-heading text-navy mb-5 flex items-center space-x-2" data-id="element-843">
                    <MapPinIcon className="w-5 h-5 text-gold-subtle" data-id="element-844" />
                    <span data-id="element-845">{block.zone}</span>
                  </h3>
                  <div className="flex flex-wrap gap-2.5" data-id="element-846">
                    {block.areas.map((area, i) => <span key={i} className="px-3.5 py-1.5 bg-warm-white border border-stone/10 rounded-full text-sm font-medium text-slate" data-id="element-847">
                        {area}
                      </span>)}
                  </div>
                </div>)}
            </div>

            <div className="text-center max-w-3xl mx-auto" data-id="element-848">
              <p className="text-slate leading-relaxed mb-8" data-id="element-849">
                Popular villa transfer routes include airport to Cap Martinet,
                airport to Roca Llisa, airport to Santa Gertrudis, airport to Es
                Cubells and airport to private fincas across the island.
              </p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-850">
                <MessageCircleIcon className="w-4 h-4" data-id="element-851" />
                <span data-id="element-852">Send villa address on WhatsApp</span>
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-853" />
              </a>
            </div>
          </div>

          {/* Popular Routes Sub-section */}
          <div className="mb-20" data-id="element-854">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-855">
              <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-856">
                Popular airport to villa transfer routes
              </h2>
            </div>

            <div className="space-y-6 mb-12" data-id="element-857">
              {[{
              name: 'Ibiza Airport → Cap Martinet',
              prices: {
                standard: '120€',
                premium: '100€',
                group: '120€'
              }
            }, {
              name: 'Ibiza Airport → Roca Llisa',
              prices: {
                standard: '120€',
                premium: '120€',
                group: '140€'
              }
            }, {
              name: 'Ibiza Airport → Santa Gertrudis',
              prices: {
                standard: '120€',
                premium: '120€',
                group: '140€'
              }
            }, {
              name: 'Ibiza Airport → Es Cubells',
              prices: {
                standard: '140€',
                premium: '140€',
                group: '160€'
              }
            }, {
              name: 'Ibiza Airport → Private finca / rural area',
              prices: {
                standard: '140€',
                premium: '140€',
                group: '160€'
              }
            }].map((route, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white border border-stone/10 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-858">
                  <div className="flex items-center space-x-2 mb-6" data-id="element-859">
                    <MapPinIcon className="w-5 h-5 text-slate" data-id="element-860" />
                    <h3 className="text-xl md:text-2xl font-heading text-navy" data-id="element-861">
                      {route.name}
                    </h3>
                  </div>

                  <div className="grid grid-cols-3 gap-px bg-stone/10 rounded-xl overflow-hidden" data-id="element-862">
                    {/* Standard */}
                    <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-863">
                      <div className="h-5 md:h-6 mb-1" data-id="element-864" />
                      <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-865">
                        STANDARD
                      </div>
                      <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-866">
                        from {route.prices.standard}
                      </div>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-867">
                        <span data-id="element-868">Get price</span>
                        <ArrowRightIcon className="w-3 h-3" data-id="element-869" />
                      </a>
                    </div>

                    {/* Premium Van */}
                    <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-870">
                      <div className="h-5 md:h-6 mb-1 flex items-center justify-center" data-id="element-871">
                        <span className="inline-flex items-center space-x-0.5 bg-gold-subtle/15 text-gold-subtle text-[6px] md:text-[9px] font-bold tracking-wider px-1.5 md:px-2.5 py-0.5 rounded-full whitespace-nowrap border border-gold-subtle/20" data-id="element-872">
                          <span data-id="element-873">⭐</span>
                          <span data-id="element-874">MOST POPULAR</span>
                        </span>
                      </div>
                      <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-875">
                        PREMIUM VAN
                      </div>
                      <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-876">
                        from {route.prices.premium}
                      </div>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-877">
                        <span data-id="element-878">Get price</span>
                        <ArrowRightIcon className="w-3 h-3" data-id="element-879" />
                      </a>
                    </div>

                    {/* Group Van */}
                    <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-880">
                      <div className="h-5 md:h-6 mb-1" data-id="element-881" />
                      <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-882">
                        GROUP VAN
                      </div>
                      <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-883">
                        from {route.prices.group}
                      </div>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-884">
                        <span data-id="element-885">Get price</span>
                        <ArrowRightIcon className="w-3 h-3" data-id="element-886" />
                      </a>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            <div className="text-center" data-id="element-887">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-888">
                <WhatsAppIcon className="w-5 h-5" data-id="element-889" />
                <span data-id="element-890">Get villa transfer price on WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Pricing Sub-section */}
          <div data-id="element-891">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-892">
              <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-893">
                Villa transfer prices
              </h2>
            </div>

            <div className="max-w-4xl mx-auto mb-8" data-id="element-894">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone/10 rounded-2xl overflow-hidden border border-stone/10" data-id="element-895">
                {/* Standard */}
                <div className="bg-white p-6 md:p-8 text-center flex flex-col items-center" data-id="element-896">
                  <div className="h-6 mb-2" data-id="element-897" />
                  <div className="text-xs font-medium tracking-wider text-stone uppercase mb-3" data-id="element-898">
                    STANDARD
                  </div>
                  <div className="text-3xl md:text-4xl font-heading text-navy mb-4" data-id="element-899">
                    from €120
                  </div>
                </div>

                {/* Premium Van */}
                <div className="bg-white p-6 md:p-8 text-center flex flex-col items-center relative" data-id="element-900">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gold-subtle" data-id="element-901"></div>
                  <div className="h-6 mb-2 flex items-center justify-center" data-id="element-902">
                    <span className="inline-flex items-center space-x-1 bg-gold-subtle/15 text-gold-subtle text-[10px] font-bold tracking-wider px-3 py-1 rounded-full whitespace-nowrap border border-gold-subtle/20" data-id="element-903">
                      <span data-id="element-904">⭐</span>
                      <span data-id="element-905">MOST POPULAR</span>
                    </span>
                  </div>
                  <div className="text-xs font-medium tracking-wider text-stone uppercase mb-3" data-id="element-906">
                    PREMIUM VAN
                  </div>
                  <div className="text-3xl md:text-4xl font-heading text-navy mb-4" data-id="element-907">
                    from €100
                  </div>
                </div>

                {/* Group Van */}
                <div className="bg-white p-6 md:p-8 text-center flex flex-col items-center" data-id="element-908">
                  <div className="h-6 mb-2" data-id="element-909" />
                  <div className="text-xs font-medium tracking-wider text-stone uppercase mb-3" data-id="element-910">
                    GROUP VAN
                  </div>
                  <div className="text-3xl md:text-4xl font-heading text-navy mb-4" data-id="element-911">
                    from €120
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center max-w-2xl mx-auto" data-id="element-912">
              <p className="text-sm text-slate mb-8" data-id="element-913">
                Remote or difficult-access villas may include a small supplement
                of 5€ to 15€, always confirmed upfront before the journey.
              </p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-914">
                <WhatsAppIcon className="w-5 h-5" data-id="element-915" />
                <span data-id="element-916">Get exact villa transfer price</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-14 md:py-20 bg-warm-cream border-y border-stone/10" data-id="element-917">
        <div className="mx-auto max-w-7xl px-5" data-id="element-918">
          <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-919">
            <h2 className="text-2xl md:text-3xl font-heading text-navy mb-3" data-id="element-920">
              Why travellers choose our Ibiza villa transfer service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-921">
            {[{
            title: 'Direct to the correct villa entrance',
            desc: 'No confusion, no wrong turn and no unnecessary phone calls.'
          }, {
            title: 'Local drivers who know remote access roads',
            desc: 'More reliable than drivers using maps alone.'
          }, {
            title: 'Fixed price before arrival',
            desc: 'No hidden extras at the end of the journey.'
          }, {
            title: 'Easy WhatsApp booking',
            desc: 'Fast quote and clear confirmation.'
          }, {
            title: 'Ideal for return journeys too',
            desc: 'Arrival, departure and multiple villa trips can be arranged.'
          }].map((feature, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl p-5 shadow-sm" data-id="element-922">
                <CheckCircle2Icon className="w-5 h-5 text-gold-subtle mb-2.5" data-id="element-923" />
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-924">
                  {feature.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed" data-id="element-925">
                  {feature.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. Concierge / Villa Managers */}
      <section className="py-16 md:py-24 bg-white" data-id="element-926">
        <div className="mx-auto max-w-4xl px-5" data-id="element-927">
          <div className="bg-warm-white border border-stone/10 rounded-3xl p-8 md:p-12 text-center md:text-left flex flex-col md:flex-row items-center gap-10" data-id="element-928">
            <div className="flex-1" data-id="element-929">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-navy/5 mb-6" data-id="element-930">
                <BriefcaseIcon className="w-6 h-6 text-navy" data-id="element-931" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading text-navy mb-4" data-id="element-932">
                For villa managers and concierge services
              </h2>
              <p className="text-slate leading-relaxed mb-6" data-id="element-933">
                If you manage villas or guest arrivals in Ibiza, we can become
                your reliable transfer partner for airport pickups, return
                journeys and guest transport during the stay.
              </p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-navy hover:text-gold-subtle font-medium transition-colors" data-id="element-934">
                <MessageCircleIcon className="w-4 h-4" data-id="element-935" />
                <span data-id="element-936">Message us to set up a partnership</span>
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-937" />
              </a>
            </div>

            <div className="w-full md:w-auto bg-white rounded-2xl p-6 shadow-sm border border-stone/10" data-id="element-938">
              <ul className="space-y-4 text-left" data-id="element-939">
                {['10% referral commission', 'Fast guest support on WhatsApp', 'Invoice available', 'Reliable service for premium guests'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-940">
                    <CheckIcon className="w-5 h-5 text-gold-subtle flex-shrink-0 mt-0.5" data-id="element-941" />
                    <span className="text-sm font-medium text-navy" data-id="element-942">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. FAQs */}
      <section className="py-16 md:py-24 bg-warm-white border-y border-stone/10" data-id="element-943">
        <div className="mx-auto max-w-3xl px-5" data-id="element-944">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-945">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-946">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl overflow-hidden" data-id="element-947">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left" data-id="element-948">
                  <span className="font-semibold text-navy pr-4" data-id="element-949">{faq.q}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-950" />
                </button>
                <AnimatePresence data-id="element-951">
                  {openFaqIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-952">
                      <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate leading-relaxed" data-id="element-953">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }} data-id="element-954" />
      </section>

      {/* 8. Reviews */}
      <section className="py-16 md:py-24 bg-white border-b border-stone/10" data-id="element-955">
        <div className="mx-auto max-w-7xl px-5" data-id="element-956">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-957">
            What clients say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-958">
            {[{
            text: 'Our villa was difficult to find and the driver arrived without calling us once. Very smooth experience.',
            author: 'Airport to villa transfer — London'
          }, {
            text: 'Reliable airport to villa transfer with clear pricing and excellent communication.',
            author: 'Family arrival to private villa — Madrid'
          }, {
            text: 'We used the service several times during our stay and it was always on time.',
            author: 'Villa guest transport during stay — Paris'
          }].map((review, index) => <div key={index} className="bg-warm-cream p-8 rounded-2xl" data-id="element-959">
                <div className="flex space-x-1 mb-4" data-id="element-960">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-gold-subtle text-gold-subtle" data-id="element-961" />)}
                </div>
                <p className="text-navy font-medium mb-6 leading-relaxed" data-id="element-962">
                  "{review.text}"
                </p>
                <p className="text-sm text-slate" data-id="element-963">— {review.author}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. Internal Links */}
      <section className="py-14 md:py-20 bg-warm-white border-b border-stone/10" data-id="element-964">
        <div className="mx-auto max-w-4xl px-5" data-id="element-965">
          <p className="text-slate text-center mb-8" data-id="element-966">
            Need more than a one-way villa transfer? We also offer airport
            pickup and private driver service across Ibiza.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-id="element-967">
            <Link to="/ibiza-airport-transfer" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-968">
              <span className="font-medium text-navy text-sm" data-id="element-969">
                Ibiza Airport Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-970" />
            </Link>
            <Link to="/ibiza-private-driver" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-971">
              <span className="font-medium text-navy text-sm" data-id="element-972">
                Private Driver Ibiza
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-973" />
            </Link>
          </div>
        </div>
      </section>

      {/* 10. Final CTA */}
      <section className="py-14 md:py-20 bg-navy relative overflow-hidden" data-id="element-974">
        <div className="relative mx-auto max-w-3xl px-5 text-center z-10" data-id="element-975">
          <h2 className="text-3xl md:text-4xl font-heading text-white mb-4 leading-tight" data-id="element-976">
            Book your Ibiza villa transfer now
          </h2>
          <p className="text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-977">
            Send your villa address, arrival time and number of passengers.
            We'll confirm your fixed price on WhatsApp.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 mb-4" data-id="element-978">
            <WhatsAppIcon className="w-5 h-5" data-id="element-979" />
            <span data-id="element-980">Get villa transfer price</span>
          </a>
          <p className="text-sm text-white/50" data-id="element-981">
            Fast reply · Fixed price · No upfront payment
          </p>
        </div>
      </section>
    </main>;
}